// src/utils/auditionDetailHelpers.js
import { parse, format, isValid } from 'date-fns';

/**
 * Calculates age from a date of birth string in MM/DD/YYYY format.
 * @param {string} dobString - Date of birth string (e.g., "03/15/1990").
 * @returns {number|string} Age in years, or '---' if dobString is invalid.
 */
export const calculateAge = (dobString) => {
    if (!dobString) return '---';
    const [month, day, year] = dobString.split('/').map(Number);
    const birthDate = new Date(year, month - 1, day); 
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    return age;
};

// Helper to format a requirement value, showing 'Any' or 'N/A' for empty/null
const formatRequirement = (value) => {
    if (!value || value.trim() === '' || value.toLowerCase() === 'any' || value.toLowerCase() === 'null') {
        return 'Any';
    }
    // For values that might be "5_6" -> "5'6\"" or "17_19" -> "17-19"
    if (typeof value === 'string' && value.includes('_')) {
        return value.replace('_', '-').replace('-', "'"); // Example: 5_6 -> 5'6"
    }
    return value;
};


/**
 * Transforms raw API audition data into a frontend-friendly format.
 * @param {object} raw - The raw audition data from the API.
 * @param {string} roleApplicationId - The ID of the role application.
 * @returns {object} Transformed audition object.
 */
export const transformAuditionData = (raw, id) => {

    if (!raw) return null;

    const projectUuid = raw.project_joget_id; // Using project_joget_id from the new API response
    const fileName = raw.projectImage || raw.projectimage; // Check both keys
    
    // Construct dynamic image URL
    const dynamicImageUrl = fileName && projectUuid
        ? `https://bma.on.joget.cloud/jw/web/client/app/bookmyaudition/1/form/download/ProjectSteperAPI/${projectUuid}/${fileName}.?timestamp=${new Date().getTime()}`
        : "";

    // const basePrice = parseFloat(raw.pricingLabel) || 600;
    // const gst = Math.round(basePrice * 0.09);
    // const totalAmount = basePrice + gst;
    const basePrice =
    Number(
        raw.final_amount ||
        raw.finalAmount ||
        raw.pricingLabel ||
        raw.price ||
        0
    );
    const gst = Math.round(basePrice * 0.09);
    const totalAmount =
    Number(
        (basePrice + gst).toFixed(2)
    );
    return {
        final_amount: raw.final_amount,
        finalAmount: raw.final_amount,
        roleLocalId: raw.c_roleLocalId || raw.roleLocalId,
        jobIdForBooking: raw.id,
        roleld: raw.c_roleld || raw.roleld,    
        rolePostBy: raw.c_userId || raw.created_by, // Use created_by as fallback
        projectName: raw.projectName || raw.project_name || "Project Name Loading...", // Use project_name
        projectPrice: basePrice,
        rawPrice: raw.pricingLabel,
        // price: basePrice,
        price: raw.final_amount || basePrice,
        gst: gst,
        totalAmount: totalAmount,
        auditionType: raw.c_auditionType || raw.audition_type || "online;facetoface;walkin", 
        allCosts: raw.allCosts || [],
        roleName: raw.c_roleName || raw.role_name || "Untitled Role", // Use role_name
        projectImageUrl: raw.projectImageUrl || dynamicImageUrl,
        // projectType: raw.projectType || raw.role_category || "General", // Use role_category
        projectType: raw.projectType || raw.projectType || raw.c_projectType || "General",
        roleCategory: raw.role_category   || raw.roleCategory  || raw.c_roleCategory || "General",
        language: raw.c_requiredLanguage || raw.required_language || "Hindi",
        shortDescription: raw.c_roleDescription ? raw.c_roleDescription.substring(0, 150) + "..." : "", // Old description format
        fullDescription: raw.c_roleDescription || raw.role_description || "", // Use role_description for full
        roleScene: raw.c_roleScene || raw.role_scene || "", // Use role_scene
        roleScript: raw.c_roleScript || raw.role_script || "", // Use role_script
        
        schedules: raw.schedules ? raw.schedules : (raw.roleScheduleJson ? JSON.parse(raw.roleScheduleJson) : []), 

        // --- All Talent Requirements ---
        // Applying formatRequirement to all:
        talent: formatRequirement(raw.c_requiredTalent || raw.required_talent || raw.talent),
        age: formatRequirement(raw.c_requiredAge || raw.required_age),
        gender: formatRequirement(raw.c_requiredGender || raw.required_gender),
        preferredLanguages: formatRequirement(raw.c_requiredLanguage || raw.required_language),
        nationality: formatRequirement(raw.c_requiredNationality || raw.required_nationality),
        skinTone: formatRequirement(raw.c_requiredSkinColor || raw.required_skin_color),
        height: formatRequirement(raw.c_requiredHeight || raw.required_height),
        bodyType: formatRequirement(raw.c_requiredBodyType ||raw.required_body_type),
        hairColor: formatRequirement(raw.c_requiredHairColor || raw.required_hair_color),
        hairType: formatRequirement(raw.c_requiredHairType || raw.required_hair_type),
        eyeColor: formatRequirement(raw.c_eyeColor || raw.required_eye_color),
        waistSize: formatRequirement(raw.c_requiredWaistSize || raw.required_waist_size),
        chestSize: formatRequirement(raw.c_requiredChestSize || raw.required_chest_size),
        dialect: formatRequirement(raw.c_requiredDialect || raw.required_dialect),
        experienceLevel: formatRequirement(raw.c_requiredExperienceLevel || raw.required_experience_level),
        specialCategory: formatRequirement(raw.required_special_category),
        
        // Ensure auditionType is an array of labels for display if it's a string like "online;walkIn"
        displayAuditionTypes: (raw.audition_type || raw.c_auditionType || "online").split(';').map(type => {
            if (type === 'online') return 'Online Video Upload';
            if (type === 'facetoface') return 'Face-to-Face Interview';
            if (type === 'walkIn') return 'Walk-In Audition';
            return type; // Fallback
        }),
        allCosts: raw.allCosts || [],

        castingDirector: {
            name: raw.createdByName || "Janet Ellis", // You'll need to fetch CD name by ID usually
            position: "Casting Director",
            profileImage: "",
            bio: raw.c_roleDescription || raw.role_description || "Experienced professional casting director.",
            profileLink: "#",
        }
    };
};